/**
 * typed-data.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { AbstractControl, FormArray, FormControl, ValidatorFn }
  from "@angular/forms";
import * as moment from "moment";

import { randomInt, valueless } from "../../support/utilities/functions";
import { DataType, SERVER_TYPES } from "./data-types";
import { StepPresentationHints } from "./presentation-hints";

/**
 * TypedDataHolder is an interface version of the class for classes that already
 * inherit but otherwise have the TypedData properties at root level.
 * (eg. IntakeStep extends AlgorithmStep but could've be a TypedData instance)
 */
export interface TypedDataHolder
{
  value : any;
  type : DataType;

  name? : string;
  cardinality? : number;
  restrictions? : any[];
  allowsDuplicates? : boolean;
  hints? : StepPresentationHints;

  id? : number;
  control? : AbstractControl;
  [other : string] : any;
}

export interface TypedDataHolderWithDefaults extends TypedDataHolder
{
  name : string;
  cardinality : number;
  restrictions : any[];
  allowsDuplicates : boolean;
  hints : StepPresentationHints;
}

/**
 * {@link TypedData} is a chunk of data with type and, optionally, cardinality
 * and restriction settings, that can be serialized and deserialized and safely
 * understood by both client and server.
 */
export class TypedData implements TypedDataHolder
{
  public name? : string;
  public control? : AbstractControl;
  public presentation? : string;

  public constructor (
    public type : DataType = "string",
    public value : any = null,
    public cardinality : number = 1,
    public restrictions : any[] = []
  )
  {
    // Nothing
  }

  public static applyDefaults (data : TypedDataHolder)
    : data is TypedDataHolderWithDefaults
  {
    if (!Array.isArray(data.restrictions))
    {
      data.restrictions = [];
    }
    if (!data.name)
    {
      data.name = data.id ? `${data.id}` : `UNKNOWN-${randomInt(10000)}`;
    }
    if (!Number.isInteger(data.cardinality))
    {
      data.cardinality = 1;
    }
    if (data.allowsDuplicates !== false)
    {
      data.allowsDuplicates = true;
    }
    data.hints = data.hints || {};
    return true;
  }

  public static create (holder : TypedDataHolder) : TypedData
  {
    const td : TypedData = new TypedData();
    Object.assign(td, holder);
    return td;
  }

  public deserialize () : void
  {
    this.value = TypedData.deserialize(this);
  }

  public static deserialize (data : TypedDataHolder) : any
  {
    // Do not modify nulls in any way; they signal cleared values.
    if (data.value === null)
    {
      return null;
    }

    // The server doesn't specifically communicate that a value is a set or list
    // but we infer it from the data's cardinality. (and nearly all sets will
    // have restrictions, so we can generally identify if uniqueness is a thing)
    const cardinality : number =
      (Number.isInteger(data.cardinality) ? data.cardinality : 1);
    switch (data.type)
    {
      case "date":
      case "datetime":
        if (cardinality === 1)
        {
          return new Date(+data.value);
        }
        else
        {
          if (!Array.isArray(data.value))
          {
            console.error(data.value);
            throw new Error("Multivalued input returned non-array data!");
          }
          const dates : Date[] = data.value.map((v : number) => {
              if (Number.isInteger(v))
              {
                return new Date(v);
              }
              if (v === null)
              {
                return null;
              }
              throw Error(`Deserialization failure: ${v} -> date`);
          });
          // console.log(data.value, dates);
          return dates;
        }
      // case "boolean":
      //   return JSON.parse(data.value);
      default:
        // Most of the other types don't need any special handling. Strings and
        // intgers definitely don't. We'll see if anything shakes out with
        // floats or other future types.
        return data.value;
    }
  }

  /**
   * Convert a unix timestamp into a string consumable by a WebKit date field.
   * Note that this assumes the data represents a *date* (not a time) in UTC.
   */
  public static dateToInput (value : number | Date) : string
  {
    return moment(value).utc().format("YYYY-MM-DD");
  }

  public static inputToDate (input : string | null) : Date | null
  {
    const m : moment.Moment = moment.utc(input, "YYYY-MM-DD");
    if (m.isValid())
    {
      return m.toDate();
    }
    return null;
  }

  public serialize () : void
  {
    this.value = TypedData.serialize(this);
  }

  public static serializeValueBy (value : any, data : TypedDataHolder) : any
  {
    return TypedData.serialize(Object.assign({}, data, {value}));
  }

  /**
   * Serialize the data in preparation for communication via JSON over websocket
   */
  public static serialize (data : TypedDataHolder) : any
  {
    if (data.restrictions && data.value === "0: null")
    {
      // Until https://github.com/angular/angular/pull/12829 in ng >=2.2.1
      data.value = null;
      return null;
    }

    const cardinality : number =
      Number.isInteger(data.cardinality) ? data.cardinality : 1;
    if (cardinality === 1)
    {
      return TypedData.serializeSingle(data.value, data.type);
    }
    return TypedData.serializeArray(data.value, data.type, cardinality);
  }

  private static serializeArray (
    value : any[],
    type : DataType,
    cardinality : number
  ) : any[]
  {
    if (!Array.isArray(value))
    {
      throw new Error("Data must be an array for multi-valued inputs");
    }
    if (cardinality > 1 && cardinality !== value.length)
    {
      throw new Error("Data size does not match fixed cardinality");
    }

    const results : any[] = value.map(
      (v : any) => TypedData.serializeSingle(v, type));
    // NULLS ARE ALLOWED!
    // if (cardinality === 0)
    // {
    //   return results.filter((v : any) => v !== null);
    // }
    return results;
  }

  private static serializeSingle (value : any, type : DataType) : any
  {
    if (value === null)
    {
      return null;
    }
    switch (type)
    {
      case "date":
        if (!(value instanceof Date))
        {
          throw new Error("Data claims to be date, but not Date object");
        }
        return value.valueOf();
      case "string":
        if (value === "")
        {
          return null;
        }
        return value;
      default:
        return value;
    }
  }

  // tslint:disable-next-line:cyclomatic-complexity
  public static compare (
    a : TypedDataHolder | null,
    b : TypedDataHolder | null) : number
  {
    if (a === null || typeof a !== "object")
    {
      return -1;
    }
    if (b === null || typeof b !== "object")
    {
      return 1;
    }
    if (a.type !== b.type || a.cardinality !== b.cardinality)
    {
      console.error(a, b);
      debugger;
      throw new Error("Cannot compare different types!");
    }

    // Always sort nulls to the bottom. This additionally helps us avoid
    // comparison function failures below.
    if (a.value === null || a.value === undefined)
    {
      return -1;
    }
    if (b.value === null || b.value === undefined)
    {
      return 1;
    }

    if (a.cardinality === -1 || a.cardinality > 1)
    {
      // for now our list comparison simply compares list lengths
      // if we start using this comparator, we may want something more exact
      return a.value.length - b.value.length;
    }
    if (a.type === "string")
    {
      return (a.value as string).localeCompare(b.value);
    }
    return a.value - b.value;
  }

  // tslint:disable-next-line:cyclomatic-complexity
  public static compareIndices (arr : TypedDataHolder, a : number, b : number)
    : number
  {
    if (arr.cardinality === 1 || !Array.isArray(arr.value))
    {
      console.error(arr, a, b);
      throw new Error("Cannot compare indices of non-array data");
    }

    const data : any[] = arr.value;
    if (!Number.isInteger(a) || a < 0 || a >= data.length
      || !Number.isInteger(b) || b < 0 || b >= data.length)
    {
      console.error(arr, a, b);
      throw new Error("Invalid indices for comparison of array data");
    }

    if (a === b)
    {
      return 0;
    }

    if (data[a] === null)
    {
      return -1;
    }
    if (data[b] === null)
    {
      return 1;
    }

    if (arr.type === "string")
    {
      return (data[a] as string).localeCompare(data[b]);
    }

    return data[a] - data[b];
  }

  public static toString (a : TypedDataHolder, value? : any) : string
  {
    if (value === undefined)
    {
      value = a.value;
    }
    switch (a.type)
    {
      case "boolean":
        return (value ? "true" : "false");
      case "date":
        return moment.utc(value as Date).format("MM/DD/YYYY");
      default:
        return `${value}`;
    }
  }

  public static hasProperties (tvLike : any) : boolean {
    return (tvLike !== null && typeof tvLike === 'object' && SERVER_TYPES.includes(tvLike.type) && "value" in tvLike);
  }
  public static guessType (value : any) : DataType | undefined {
    switch (typeof value) {
      case "boolean":
        return "boolean";
      case "string":
        return "string";
      case "number":
        // safest guess for number
        return "float";
      case "object":
        return undefined;
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/typed-data.ts